import { useState, useEffect } from "react";
import { Loader2, RefreshCw } from "lucide-react";
import { signOut } from "../lib/supabase";

interface LoadingScreenProps {
    message?: string;
    timeout?: number;
}

export default function LoadingScreen({
    message = "Loading...",
    timeout = 5000
}: LoadingScreenProps) {
    const [showReset, setShowReset] = useState(false);

    useEffect(() => {
        const timer = setTimeout(() => {
            setShowReset(true);
        }, timeout);

        return () => clearTimeout(timer);
    }, [timeout]);

    const handleReset = async () => {
        try {
            // Clear all storage
            localStorage.clear();
            sessionStorage.clear();

            // Force sign out
            await signOut();
        } catch (e) {
            console.error("Error during reset:", e);
        } finally {
            // Force reload
            window.location.href = '/';
        }
    };

    return (
        <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 p-4">
            <div className="text-center max-w-md w-full">
                <div className="relative mb-6">
                    <div className="w-12 h-12 border-4 border-gray-200 border-t-trestune-primary rounded-full animate-spin mx-auto"></div>
                </div>

                <h2 className="text-xl font-semibold text-gray-900 mb-2">{message}</h2>

                {showReset && (
                    <div className="mt-8 animate-in fade-in duration-500">
                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4 text-left">
                            <p className="text-sm text-yellow-800 mb-2">
                                Taking longer than expected? The application might be stuck.
                            </p>
                        </div>

                        <button
                            onClick={handleReset}
                            className="flex items-center justify-center w-full px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 shadow-sm transition-colors"
                        >
                            <RefreshCw className="w-4 h-4 mr-2" />
                            Reset Application
                        </button>
                        <p className="text-xs text-gray-500 mt-2">
                            This will clear your session and reload the page.
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
}
